import { UserOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import styled from 'styled-components';

import type CommentStore from 'stores/Comment';
import { textareaHeight } from 'utils/Textarea';

import CommentLayout from './CommentLayout';
import { type UserLike } from './types';

const AVATAR_SIZE_PX = 32;

const CommentInput = styled.textarea`
    border: 1px solid #ddd;
    border-radius: 5px;
    width: 100%;
    padding: 8px 11px;
    resize: none;
    display: flex;
`;

const Thumbnail = styled.img`
    width: ${AVATAR_SIZE_PX}px;
    height: ${AVATAR_SIZE_PX}px;
    border-radius: 50%;
`;

const ThumbnailPlaceholder = styled.div`
    width: ${AVATAR_SIZE_PX}px;
    height: ${AVATAR_SIZE_PX}px;
    border-radius: 50%;
    border: 1px solid #ededed;
    background: #f6f2f2;
    font-size: 24px;
    text-align: center;
    color: rgba(0, 0, 0, 0.14);
    overflow: hidden;
`;

const Container = styled.div`
    align-items: center;
    margin: 0 10px 10px 10px;
    padding: 6px;
    border: 1px solid #eee;
    border-radius: 5px;
    background: white;

    &:first-child {
        margin: 10px;
    }
`;

export interface CommentProps {
    comment: CommentStore;
    user: UserLike;
}

function Comment({ comment, user }: CommentProps) {
    const commentInput = useRef<HTMLTextAreaElement>(null);

    // Note the two action groups below differ on purpose: only the first
    // checks `authenticated`. Editing can only be entered from that
    // branch, so the second is reachable only after the check has already
    // passed.
    const isOwnComment = comment.data.owner === user.id;

    // Written straight to the observable: the store is configured with
    // enforceActions: 'never', so a setter would buy nothing here.
    const commitEdit = async (content: string) => {
        comment.editing = false;
        comment.setContent(content);
        await comment.edit(content);
    };

    const handleSaveClick = () => {
        void commitEdit(commentInput.current?.value ?? '');
    };

    const handleEditClick = () => {
        comment.editing = true;
    };

    const handleCancelClick = () => {
        comment.editing = false;
    };

    // Returned rather than voided, unlike the DOM handlers above.
    // Popconfirm passes this to antd's ActionButton, which spins the OK
    // button while the promise is pending and only dismisses the popover
    // once it resolves.  Voiding it would close the popover instantly and
    // lose that feedback.
    const handleDelete = async () => {
        await comment.delete();
    };

    // Grow the textarea as the user types. Written straight to the element
    // rather than through state, so typing doesn't re-render the comment.
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        e.target.style.height = textareaHeight(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (e.currentTarget.value !== '') {
                void commitEdit(e.currentTarget.value);
            }
        }
    };

    const avatar = comment.ownerPhotoURL ? (
        <Thumbnail src={comment.ownerPhotoURL} alt="user" />
    ) : (
        <ThumbnailPlaceholder style={{ background: comment.thumbnailColor }}>
            <UserOutlined />
        </ThumbnailPlaceholder>
    );

    const content = comment.editing ? (
        <CommentInput
            ref={commentInput}
            style={{ height: textareaHeight(comment.data.content) }}
            defaultValue={comment.data.content}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
        />
    ) : (
        comment.data.content
    );

    const actions: React.ReactNode[] = [];

    if (user.authenticated && isOwnComment && !comment.editing) {
        actions.push(
            <span key="Edit" onClick={handleEditClick}>
                Edit
            </span>,
            // Popconfirm manages its own visibility and already dismisses
            // on an outside click, so this needs no open/visible wiring of
            // its own.
            <Popconfirm
                key="Delete"
                title="Are you sure you want to delete this comment?"
                onConfirm={handleDelete}
                okText="Yes"
                cancelText="No"
            >
                <span>Delete</span>
            </Popconfirm>
        );
    }

    if (isOwnComment && comment.editing) {
        actions.push(
            <span key="Cancel" onClick={handleCancelClick}>
                Cancel
            </span>,
            <span key="Save" onClick={handleSaveClick}>
                Save
            </span>
        );
    }

    return (
        <Container>
            <CommentLayout
                actions={actions}
                author={comment.ownerDisplayName}
                avatar={avatar}
                content={content}
                datetime={
                    comment.datetime + (comment.edited ? ' (edited)' : '')
                }
            />
        </Container>
    );
}

export default observer(Comment);
